/**
 * Cell evaluation pipeline for the Excel widget.
 *
 * Every "=" cell passes one gate before anything else happens: all of
 * its `@` references must resolve. A cell that fails the gate errors
 * without an agent call or an execute call. A cell that passes then
 * branches on whether any English survived tokenizing:
 *
 *   no English  → compose the DSL locally, execute. NEVER calls an LLM.
 *   English     → send the body with references inlined, verify the
 *                 reply left them untouched, cache the re-masked
 *                 template, execute the verified reply.
 *
 * Literal cells stay at status='idle'. See excelFormula.ts and
 * docs/market-monitor/excel_cell_references.md.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type DslExecuteResponse,
  type DslResultPayload,
  executeDsl as executeDslDefault,
  generateDsl as generateDslDefault,
} from '../../../services/DslService';
import { useMarketMonitorLiveContext } from '../live/MarketMonitorLiveContext';
import {
  isDslCell,
  parseLiteralNumber,
  promptOf,
  type ExcelCell,
  type ExcelWidgetConfig,
} from './excelConfig';
import {
  buildTranslationRequest,
  composeCellDsl,
  maskTranslation,
  parseFormulaBody,
} from './excelFormula';

export type EvaluationStatus =
  | { status: 'idle' }
  /** A referenced cell is valid but not translated yet — not an error. */
  | { status: 'waiting' }
  | { status: 'translating' }
  | { status: 'executing'; resolvedDsl: string }
  | { status: 'ok'; display: string; resolvedDsl: string; raw: DslResultPayload }
  | {
      status: 'error';
      stage: 'reference' | 'translate' | 'execute';
      message: string;
      resolvedDsl?: string;
    };

export interface UseExcelEvaluationsOptions {
  /** Test seam — defaults to the production DslService implementations. */
  generateDsl?: (prompt: string, signal?: AbortSignal) => Promise<string>;
  executeDsl?: (expression: string) => Promise<DslExecuteResponse>;
  /** Notified when stage-1 produces a fresh DSL — host persists it. */
  onResolved?: (r: number, c: number, dsl: string) => void;
  /** Optional refresh epoch — bump to force every cell to re-execute. */
  refreshEpoch?: number;
  /** Optional retranslate epoch — bump to force stage 1 + 2 from scratch. */
  retranslateEpoch?: number;
}

/** Last non-null entry of a number-or-null array. Returns null if empty / all-null. */
export function lastNonNull(values: ReadonlyArray<number | null>): number | null {
  for (let i = values.length - 1; i >= 0; i -= 1) {
    const v = values[i];
    if (v !== null && v !== undefined && Number.isFinite(v)) return v;
  }
  return null;
}

const NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const DASH = '—';

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return DASH;
  return NUMBER_FORMATTER.format(value);
}

/** Render rules from §3.3. */
export function formatDisplay(payload: DslResultPayload): string {
  switch (payload.type) {
    case 'time_series': {
      const last = lastNonNull(payload.values);
      return last === null ? DASH : formatNumber(last);
    }
    case 'scalar':
      return formatNumber(payload.value);
    case 'text':
      return payload.value;
    case 'null':
      return DASH;
    default:
      return DASH;
  }
}

export type EvaluationsMap = Map<string, EvaluationStatus>;

const cellKey = (r: number, c: number): string => `${r}:${c}`;

interface CellRequest {
  r: number;
  c: number;
  cachedTemplate: string | null;
}

const buildRequests = (cells: ExcelCell[][]): CellRequest[] => {
  const out: CellRequest[] = [];
  for (let r = 0; r < cells.length; r += 1) {
    for (let c = 0; c < cells[r].length; c += 1) {
      const cell = cells[r][c];
      if (!isDslCell(cell)) continue;
      out.push({ r, c, cachedTemplate: cell.resolvedDsl?.trim() || null });
    }
  }
  return out;
};

/**
 * Every cell's content, including literal cells — a literal feeding a
 * formula must retrigger evaluation, and a freshly cached translation
 * is what unblocks any cell waiting on it.
 */
const buildContentKey = (cells: ExcelCell[][]): string => {
  const parts: string[] = [];
  for (let r = 0; r < cells.length; r += 1) {
    for (let c = 0; c < cells[r].length; c += 1) {
      const cell = cells[r][c];
      parts.push(`${r}:${c}:${cell.raw}:${cell.resolvedDsl ?? ''}`);
    }
  }
  return parts.join('|');
};

const errorMessage = (caught: unknown): string => {
  if (caught instanceof Error) return caught.message;
  if (typeof caught === 'string') return caught;
  try {
    return JSON.stringify(caught);
  } catch {
    return 'unknown error';
  }
};

export function useExcelEvaluations(
  config: ExcelWidgetConfig,
  options: UseExcelEvaluationsOptions = {},
): EvaluationsMap {
  const generate = options.generateDsl ?? generateDslDefault;
  const execute = options.executeDsl ?? executeDslDefault;
  const onResolved = options.onResolved;
  const refreshEpoch = options.refreshEpoch ?? 0;
  const retranslateEpoch = options.retranslateEpoch ?? 0;

  const [evaluations, setEvaluations] = useState<EvaluationsMap>(() => new Map());

  const requests = useMemo(() => buildRequests(config.cells), [config.cells]);
  const cellsKey = useMemo(() => buildContentKey(config.cells), [config.cells]);

  const generateRef = useRef(generate);
  const executeRef = useRef(execute);
  const onResolvedRef = useRef(onResolved);
  useEffect(() => { generateRef.current = generate; }, [generate]);
  useEffect(() => { executeRef.current = execute; }, [execute]);
  useEffect(() => { onResolvedRef.current = onResolved; }, [onResolved]);

  // Survive across effect runs: a cell finishing its translation changes
  // the config, which re-runs the effect so dependents can unblock. That
  // re-run must not re-issue calls the previous pass already made.
  const translationsRef = useRef<Map<string, Promise<string>>>(new Map());
  const settledRef = useRef<Map<string, string>>(new Map());
  const refreshSeenRef = useRef(refreshEpoch);
  const retranslateSeenRef = useRef(retranslateEpoch);

  const setCell = useCallback((r: number, c: number, status: EvaluationStatus) => {
    setEvaluations((prev) => {
      const next = new Map(prev);
      next.set(cellKey(r, c), status);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const snapshot = config;
    const live = new Set(requests.map((r) => cellKey(r.r, r.c)));

    setEvaluations((prev) => {
      let mutated = false;
      const next = new Map<string, EvaluationStatus>();
      prev.forEach((value, key) => {
        if (live.has(key)) {
          next.set(key, value);
        } else {
          mutated = true;
        }
      });
      return mutated ? next : prev;
    });

    settledRef.current.forEach((_dsl, key) => {
      if (!live.has(key)) settledRef.current.delete(key);
    });

    const forceTranslate = retranslateEpoch !== retranslateSeenRef.current;
    const forceExecute = refreshEpoch !== refreshSeenRef.current;
    retranslateSeenRef.current = retranslateEpoch;
    refreshSeenRef.current = refreshEpoch;
    if (forceTranslate) translationsRef.current.clear();
    if (forceTranslate || forceExecute) settledRef.current.clear();

    if (requests.length === 0) return () => { cancelled = true; };

    const safeSet = (r: number, c: number, status: EvaluationStatus): void => {
      if (cancelled) return;
      setCell(r, c, status);
    };

    const executeCache = new Map<string, Promise<DslExecuteResponse>>();

    const translateOnce = (prompt: string): Promise<string> => {
      const hit = translationsRef.current.get(prompt);
      if (hit) return hit;
      const promise = generateRef.current(prompt);
      translationsRef.current.set(prompt, promise);
      return promise;
    };

    const executeOnce = (dsl: string): Promise<DslExecuteResponse> => {
      const hit = executeCache.get(dsl);
      if (hit) return hit;
      const promise = executeRef.current(dsl);
      executeCache.set(dsl, promise);
      return promise;
    };

    /**
     * Produce the executable DSL for one cell. Deterministic cells never
     * reach the agent; English cells send their body with references
     * inlined, and the reply is verified before it is trusted.
     */
    const resolveExecutable = async (
      req: CellRequest,
    ): Promise<string | null> => {
      const { r, c, cachedTemplate } = req;

      const parsed = parseFormulaBody(promptOf(snapshot.cells[r][c]));
      if (parsed.kind === 'error') {
        safeSet(r, c, {
          status: 'error',
          stage: 'reference',
          message: parsed.error.message,
        });
        return null;
      }

      if (!parsed.parsed.hasEnglish || (cachedTemplate !== null && !forceTranslate)) {
        const composed = composeCellDsl(snapshot, r, c);
        if (composed.kind === 'pending') {
          safeSet(r, c, { status: 'waiting' });
          return null;
        }
        if (composed.kind === 'error') {
          safeSet(r, c, {
            status: 'error',
            stage: 'reference',
            message: composed.error.message,
          });
          return null;
        }
        return composed.dsl;
      }

      const request = buildTranslationRequest(snapshot, r, c);
      if (request.kind === 'pending') {
        safeSet(r, c, { status: 'waiting' });
        return null;
      }
      if (request.kind === 'error') {
        safeSet(r, c, {
          status: 'error',
          stage: 'reference',
          message: request.error.message,
        });
        return null;
      }

      // A re-run triggered by some other cell must not flicker a cell
      // that already has a result back to a loading state.
      if (!settledRef.current.has(cellKey(r, c))) {
        safeSet(r, c, { status: 'translating' });
      }

      let returned: string;
      try {
        returned = (await translateOnce(request.request.prompt)).trim();
      } catch (caught) {
        if (!cancelled) {
          safeSet(r, c, {
            status: 'error',
            stage: 'translate',
            message: errorMessage(caught),
          });
        }
        return null;
      }
      if (cancelled) return null;

      if (!returned) {
        safeSet(r, c, {
          status: 'error',
          stage: 'translate',
          message: 'DSL agent returned an empty translation.',
        });
        return null;
      }

      const masked = maskTranslation(returned, request.request.spans);
      if (masked.kind === 'error') {
        safeSet(r, c, {
          status: 'error',
          stage: 'translate',
          message: masked.error.message,
        });
        return null;
      }

      if (masked.template !== cachedTemplate) {
        onResolvedRef.current?.(r, c, masked.template);
      }
      return returned;
    };

    const runRequest = async (req: CellRequest): Promise<void> => {
      const { r, c } = req;
      const key = cellKey(r, c);

      const executable = await resolveExecutable(req);
      if (executable === null || cancelled) return;

      if (settledRef.current.get(key) === executable) return;

      safeSet(r, c, { status: 'executing', resolvedDsl: executable });

      try {
        const response = await executeOnce(executable);
        if (cancelled) return;
        if (response.error) {
          safeSet(r, c, {
            status: 'error',
            stage: 'execute',
            message: response.error.message,
            resolvedDsl: executable,
          });
          return;
        }
        if (!response.result) {
          safeSet(r, c, {
            status: 'error',
            stage: 'execute',
            message: 'DSL execution returned no result.',
            resolvedDsl: executable,
          });
          return;
        }
        settledRef.current.set(key, executable);
        safeSet(r, c, {
          status: 'ok',
          display: formatDisplay(response.result),
          resolvedDsl: executable,
          raw: response.result,
        });
      } catch (caught) {
        if (cancelled) return;
        safeSet(r, c, {
          status: 'error',
          stage: 'execute',
          message: errorMessage(caught),
          resolvedDsl: executable,
        });
      }
    };

    void Promise.allSettled(requests.map((req) => runRequest(req)));

    return () => {
      cancelled = true;
    };
    // cellsKey covers (raw, position) shape; epochs force re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellsKey, refreshEpoch, retranslateEpoch, setCell]);

  return evaluations;
}

export interface ExcelLiveOverlay {
  display: string;
  numericValue: number | null;
  tickEpoch: number;
}

/**
 * Reduce a DSL result payload to a single number (for conditional
 * formatting). Time series collapse to their last non-null point;
 * scalar uses its `value`; text / null produce `null`.
 */
export function payloadNumericValue(payload: DslResultPayload): number | null {
  switch (payload.type) {
    case 'time_series':
      return lastNonNull(payload.values);
    case 'scalar':
      return Number.isFinite(payload.value) ? payload.value : null;
    default:
      return null;
  }
}

export { parseLiteralNumber };

/**
 * Resolve a cell's effective numeric value for conditional formatting.
 * Priority: live overlay tick > evaluator OK > literal raw. Returns
 * null when no number can be derived.
 */
export function numericValueOf(
  cell: ExcelCell,
  status: EvaluationStatus | undefined,
  overlay: ExcelLiveOverlay | undefined,
): number | null {
  if (overlay && overlay.numericValue !== null && Number.isFinite(overlay.numericValue)) {
    return overlay.numericValue;
  }
  if (status && status.status === 'ok') {
    const fromPayload = payloadNumericValue(status.raw);
    if (fromPayload !== null) return fromPayload;
  }
  if (isDslCell(cell)) return null;
  return parseLiteralNumber(cell.raw);
}

const overlayTagId = (instanceId: string, key: string): string =>
  `excel:${instanceId}:${key}`;

export function useExcelLiveOverlay(
  instanceId: string,
  evaluations: EvaluationsMap,
): Map<string, ExcelLiveOverlay> {
  const live = useMarketMonitorLiveContext();
  const subscribedRef = useRef<Map<string, string>>(new Map());
  const tickEpochRef = useRef<Map<string, number>>(new Map());
  const [overlay, setOverlay] = useState<Map<string, ExcelLiveOverlay>>(
    () => new Map(),
  );

  useEffect(() => {
    const subs = subscribedRef.current;
    const client = live.client;

    if (!live.enabled || !client) {
      if (subs.size > 0) {
        if (client) {
          subs.forEach((tagId) => {
            client.unsubscribe(tagId);
            live.resetTagState(tagId);
          });
        }
        subs.clear();
        tickEpochRef.current.clear();
        setOverlay((prev) => (prev.size === 0 ? prev : new Map()));
      }
      return;
    }

    const desired = new Map<string, string>();
    evaluations.forEach((value, key) => {
      if (value.status === 'ok' && value.resolvedDsl) {
        desired.set(key, value.resolvedDsl);
      }
    });

    const toUnsubscribe: string[] = [];
    subs.forEach((_tagId, key) => {
      if (!desired.has(key)) toUnsubscribe.push(key);
    });
    for (const key of toUnsubscribe) {
      const tagId = subs.get(key);
      if (tagId) {
        client.unsubscribe(tagId);
        live.resetTagState(tagId);
      }
      subs.delete(key);
      tickEpochRef.current.delete(key);
    }

    desired.forEach((dsl, key) => {
      if (subs.has(key)) return;
      const tagId = overlayTagId(instanceId, key);
      client.subscribe(tagId, dsl);
      subs.set(key, tagId);
    });
  }, [evaluations, instanceId, live.enabled, live.client, live.resetTagState]);

  useEffect(() => {
    if (!live.enabled) return;
    const subs = subscribedRef.current;
    setOverlay((prev) => {
      const next = new Map<string, ExcelLiveOverlay>();
      subs.forEach((tagId, cellKey) => {
        const state = live.tagStates[tagId];
        if (!state || !state.result) return;
        const newDisplay = formatDisplay(state.result);
        const newNumeric = payloadNumericValue(state.result);
        const previous = prev.get(cellKey);
        let epoch: number;
        if (
          previous &&
          previous.display === newDisplay &&
          previous.numericValue === newNumeric
        ) {
          epoch = previous.tickEpoch;
        } else {
          epoch = (tickEpochRef.current.get(cellKey) ?? 0) + 1;
          tickEpochRef.current.set(cellKey, epoch);
        }
        next.set(cellKey, {
          display: newDisplay,
          numericValue: newNumeric,
          tickEpoch: epoch,
        });
      });

      if (prev.size === next.size) {
        let same = true;
        for (const [key, val] of next) {
          const p = prev.get(key);
          if (
            !p ||
            p.display !== val.display ||
            p.numericValue !== val.numericValue ||
            p.tickEpoch !== val.tickEpoch
          ) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return next;
    });
  }, [live.enabled, live.tagStates]);

  useEffect(() => {
    return () => {
      const subs = subscribedRef.current;
      const client = live.client;
      if (client) {
        subs.forEach((tagId) => {
          client.unsubscribe(tagId);
          live.resetTagState(tagId);
        });
      }
      subs.clear();
      tickEpochRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return overlay;
}
